/** Parent filters for the management {@link DataTable}. */
import * as React from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SearchInput } from '@/components/forms/SearchInput';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { ManagedByMultiSelect } from '@/components/common/ManagedByMultiSelect';
import {
  type ParentFilterGroupId,
  type ParentFilters,
  type ParentTabView,
  type ParentStatusFilterKey,
  PARENT_REGISTRATION_STEP_OPTIONS,
  PARENT_STATUS_FILTER_CHIP_CONFIG,
  getParentStatusFilterKeys,
} from './parent-configs';

export type { ParentFilterGroupId, ParentFilters } from './parent-configs';
export {
  DEFAULT_PARENT_FILTERS,
  ALL_PARENT_FILTER_GROUP_IDS,
  PARENT_FILTER_LABELS,
} from './parent-configs';

/** Props for {@link ParentFiltersContent}. */
interface Props {
  filters: ParentFilters;
  visibleFilterIds: ParentFilterGroupId[];
  onFilterChange: (patch: Partial<ParentFilters>) => void;
  activeTab?: ParentTabView;
}

/** Filter panel for the parents {@link DataTable}. */
export const ParentFiltersContent: React.FC<Props> = ({
  filters,
  visibleFilterIds,
  onFilterChange,
  activeTab,
}) => {
  const show = (id: ParentFilterGroupId) => visibleFilterIds.includes(id);

  const registrationDateFrom =
    activeTab === 'parents'
      ? filters.parentsRegistrationDateFrom
      : filters.preRegisteredRegistrationDateFrom;
  const registrationDateTo =
    activeTab === 'parents'
      ? filters.parentsRegistrationDateTo
      : filters.preRegisteredRegistrationDateTo;

  const handleRegistrationDateFromChange = (val: string | undefined) => {
    if (activeTab === 'parents') {
      onFilterChange({ parentsRegistrationDateFrom: val || undefined });
    } else {
      onFilterChange({ preRegisteredRegistrationDateFrom: val || undefined });
    }
  };

  const handleRegistrationDateToChange = (val: string | undefined) => {
    if (activeTab === 'parents') {
      onFilterChange({ parentsRegistrationDateTo: val || undefined });
    } else {
      onFilterChange({ preRegisteredRegistrationDateTo: val || undefined });
    }
  };

  return (
    <div className="filter-groups-container">
      {show('filter-onboarding-status') && (
        <StatusFilter filters={filters} onFilterChange={onFilterChange} activeTab={activeTab} />
      )}

      {show('filter-registration-step') && activeTab === 'pre_registered' && (
        <div className="filter-group">
          <span className="filter-group-label">Étape d'inscription</span>
          <div className="filter-options">
            {PARENT_REGISTRATION_STEP_OPTIONS.map((opt) => {
              const isActive = (filters.stepKeys ?? []).includes(opt.value);
              const Icon = opt.icon;
              return (
                <Button
                  key={opt.value}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={{ '--filter-color': opt.color } as React.CSSProperties}
                  onClick={() => {
                    const current = filters.stepKeys ?? [];
                    onFilterChange({
                      stepKeys: isActive
                        ? current.filter((k) => k !== opt.value)
                        : [...current, opt.value],
                    });
                  }}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {opt.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-search') && (
        <div className="filter-group">
          <span className="filter-group-label">Recherche</span>
          <div className="relative w-64">
            <SearchInput
              size="sm"
              shortcut={false}
              placeholder="Nom, prénom, email ou téléphone..."
              value={filters.search ?? ''}
              onChange={(e) => onFilterChange({ search: e.target.value || undefined })}
              onClear={() => onFilterChange({ search: undefined })}
            />
          </div>
        </div>
      )}

      {show('filter-registration-date') && (
        <div className="filter-group">
          <span className="filter-group-label">Date d'inscription</span>
          <div className="w-auto">
            <DateRangeInput
              layout="horizontal"
              showConnector={true}
              compact={true}
              hidePickerLabels={true}
              showPresets={false}
              startDate={registrationDateFrom}
              endDate={registrationDateTo}
              onStartDateChange={(val) => handleRegistrationDateFromChange(val || undefined)}
              onEndDateChange={(val) => handleRegistrationDateToChange(val || undefined)}
            />
          </div>
        </div>
      )}

      {show('filter-managed-by') && (
        <div className="filter-group">
          <span className="filter-group-label">Géré par</span>
          <div className="w-64">
            <ManagedByMultiSelect
              value={filters.managedByIds}
              onChange={(val) => onFilterChange({ managedByIds: val })}
              inputSize="sm"
            />
          </div>
        </div>
      )}

      {show('filter-children-count') && activeTab === 'parents' && (
        <div className="filter-group">
          <span className="filter-group-label">Nombre d'enfants</span>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'w-7 h-7 p-0 rounded-md flex items-center justify-center transition-all',
                filters.childrenCount === undefined || filters.childrenCount <= 1
                  ? 'opacity-40 cursor-not-allowed'
                  : '',
              )}
              onClick={() => {
                if (filters.childrenCount === undefined || filters.childrenCount <= 1) {
                  onFilterChange({ childrenCount: undefined });
                } else {
                  onFilterChange({ childrenCount: filters.childrenCount - 1 });
                }
              }}
              disabled={filters.childrenCount === undefined || filters.childrenCount <= 1}
            >
              <Minus className="w-3.5 h-3.5" />
            </Button>
            <input
              type="number"
              min={0}
              value={filters.childrenCount ?? ''}
              placeholder="—"
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') {
                  onFilterChange({ childrenCount: undefined });
                } else {
                  const n = Number.parseInt(val, 10);
                  if (!Number.isNaN(n) && n >= 0) onFilterChange({ childrenCount: n });
                }
              }}
              className="w-10 h-7 text-center text-sm font-semibold tabular-nums rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <Button
              variant="ghost"
              size="sm"
              className="w-7 h-7 p-0 rounded-md flex items-center justify-center transition-all"
              onClick={() => onFilterChange({ childrenCount: (filters.childrenCount ?? 0) + 1 })}
            >
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/** Status chips for the current parent tab. */
const StatusFilter: React.FC<{
  filters: ParentFilters;
  onFilterChange: (patch: Partial<ParentFilters>) => void;
  activeTab?: ParentTabView;
}> = ({ filters, onFilterChange, activeTab }) => {
  const statusKeys = getParentStatusFilterKeys(activeTab);
  const locked = statusKeys.length <= 1;

  function renderStatusChip(statusKey: ParentStatusFilterKey): React.ReactNode {
    return (
      <ParentStatusChip
        key={statusKey}
        statusKey={statusKey}
        isSelected={filters.userStatus === statusKey}
        locked={locked}
        onFilterChange={onFilterChange}
      />
    );
  }

  if (statusKeys.length === 0) return null;

  return (
    <div className="filter-group">
      <span className="filter-group-label">Statut</span>
      <div className="filter-options">{statusKeys.map(renderStatusChip)}</div>
    </div>
  );
};

/** One parent status filter chip. */
function ParentStatusChip({
  statusKey,
  isSelected,
  locked,
  onFilterChange,
}: Readonly<{
  statusKey: ParentStatusFilterKey;
  isSelected: boolean;
  locked: boolean;
  onFilterChange: (patch: Partial<ParentFilters>) => void;
}>): React.ReactNode {
  const config = PARENT_STATUS_FILTER_CHIP_CONFIG[statusKey];
  const Icon = config.icon;
  const showActive = locked || isSelected;

  function handleClick(): void {
    if (locked) return;
    onFilterChange({ userStatus: isSelected ? undefined : statusKey });
  }

  return (
    <Button
      variant="custom"
      size="sm"
      disabled={locked}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
        showActive ? 'filter-badge-active' : 'filter-badge-inactive',
        locked && 'opacity-60 cursor-not-allowed',
      )}
      style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
      onClick={handleClick}
    >
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </Button>
  );
}
